import type { WorkspaceGrant } from './workspaceAccess'
import { workerPerformanceLink } from './workerPerformance'

type Access = WorkspaceGrant | 'admin'
type Badge =
  'scheduling_conversations' | 'crm_conversations' | 'quickbooks_payments'
type WorkspaceLink = {
  label: string
  to: string
  icon: string
  access?: Access
  count?: Badge
}
export type WorkspaceGroup = { label: string; items: WorkspaceLink[] }

export const workspaceMenu: WorkspaceGroup[] = [
  {
    label: 'Home',
    items: [
      { label: 'Today', to: '/', icon: 'pi-home' },
      {
        label: 'Assigned tasks',
        to: '/manager/tasks',
        icon: 'pi-bullseye',
        access: 'home_work',
      },
      {
        label: 'Pending to collect',
        to: '/manager/to-collect',
        icon: 'pi-dollar',
        access: 'home_work',
      },
      {
        label: 'Documents to approve',
        to: '/manager/documents',
        icon: 'pi-file-plus',
        access: 'home_work',
      },
    ],
  },
  {
    label: 'Marketing',
    items: [
      {
        label: 'Inquiry',
        to: '/leads/tracking',
        icon: 'pi-phone',
        access: 'marketing',
      },
      {
        label: 'Lead',
        to: '/leads',
        icon: 'pi-user-plus',
        access: 'marketing',
      },
      {
        label: 'Estimate',
        to: '/leads/estimates',
        icon: 'pi-calculator',
        access: 'marketing',
      },
      {
        label: 'Contract',
        to: '/manager/contracts',
        icon: 'pi-pen-to-square',
        access: 'contracts',
      },
      {
        label: 'Reports',
        to: '/leads/reports',
        icon: 'pi-chart-bar',
        access: 'marketing',
      },
    ],
  },
  {
    label: 'Manager',
    items: [
      {
        label: 'Projects',
        to: '/manager/projects',
        icon: 'pi-briefcase',
        access: 'projects',
      },
      {
        label: 'Customer list',
        to: '/manager/customers',
        icon: 'pi-users',
        access: 'projects',
      },
      {
        label: 'Adjuster list',
        to: '/manager/adjusters',
        icon: 'pi-star',
        access: 'projects',
      },
      {
        label: 'Field jobs',
        to: '/manager/field-jobs',
        icon: 'pi-camera',
        access: 'projects',
      },
      {
        label: 'Photo activity',
        to: '/manager/photo-activity',
        icon: 'pi-history',
        access: 'projects',
      },
      {
        label: 'QuickBooks payments',
        to: '/manager/quickbooks-payments',
        icon: 'pi-dollar',
        access: 'projects',
        count: 'quickbooks_payments',
      },
    ],
  },
  {
    label: 'Production',
    items: [
      {
        label: 'Projects',
        to: '/scheduling/projects',
        icon: 'pi-briefcase',
        access: 'scheduling',
      },
      {
        label: 'Schedule',
        to: '/scheduling/calendar',
        icon: 'pi-calendar',
        access: 'scheduling',
      },
      {
        label: 'Conversations',
        to: '/scheduling/conversations',
        icon: 'pi-comments',
        access: 'scheduling',
        count: 'scheduling_conversations',
      },
      {
        label: 'Time tracking',
        to: '/scheduling/time-tracking',
        icon: 'pi-map-marker',
        access: 'admin',
      },
    ],
  },
  {
    label: 'Campaigns',
    items: [
      {
        label: 'Campaigns',
        to: '/crm/campaigns',
        icon: 'pi-megaphone',
        access: 'campaigns',
      },
      {
        label: 'Campaign replies',
        to: '/crm/conversations',
        icon: 'pi-comments',
        access: 'campaigns',
        count: 'crm_conversations',
      },
    ],
  },
  {
    label: 'Legal',
    items: [
      {
        label: 'Red Flag Claims',
        to: '/legal/red-flag-claims',
        icon: 'pi-flag',
        access: 'legal',
      },
      {
        label: 'Liens Deadlines',
        to: '/legal/liens-deadlines',
        icon: 'pi-briefcase',
        access: 'legal',
      },
      {
        label: 'Lawsuits & Complaints',
        to: '/legal/lawsuits-complaints',
        icon: 'pi-hammer',
        access: 'legal',
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Users', to: '/users', icon: 'pi-user', access: 'admin' },
      { label: 'Setup', to: '/setup', icon: 'pi-wrench', access: 'admin' },
      {
        ...workerPerformanceLink,
        icon: 'pi-chart-bar',
        access: 'call_tools',
      },
      {
        label: 'Call log',
        to: '/comms/calls',
        icon: 'pi-list',
        access: 'call_tools',
      },
    ],
  },
]

export function workspaceLink(fullPath: string, groups = workspaceMenu) {
  const [pathname = '/', query] = fullPath.split('#')[0]!.split('?')
  const path = pathname.replace(/\/+$/, '') || '/'
  const params = new URLSearchParams(query)
  let match: { section: string; item: WorkspaceLink; base: string } | undefined
  for (const group of groups) {
    for (const item of group.items) {
      // Lists live under Campaigns in the menu, but keep their own /crm URLs.
      if (
        item.to === '/crm/campaigns' &&
        (path === '/crm/contact-lists' ||
          path.startsWith('/crm/contact-lists/'))
      )
        return { section: group.label, item, base: '/crm' }
      const [base = '/', search] = item.to.split('?')
      if (path !== base && !path.startsWith(base + '/')) continue
      if (
        search &&
        (path !== base ||
          [...new URLSearchParams(search)].some(
            ([k, v]) => params.get(k) !== v
          ))
      )
        continue
      if (!match || item.to.length > match.item.to.length)
        match = { section: group.label, item, base }
    }
  }
  return match
}
